import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { ArrowForward as ArrowIcon, Hub as HubIcon } from '@mui/icons-material';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import { FunctionPerformance as Perf, performanceApi } from '../../services/performanceApi';
import { usePersistentState } from '../../utils/usePersistentState';
import { isAdminUser } from '../../utils/auth';
import { ReliabilityDialog } from '../reliability/ReliabilityDialog';
import { SectionTitle, StatCard, fmtBucket, fmtCredits, fmtMs, fmtNum, fmtPct, fmtS } from './common';
import { PlatformFunctionDialog } from './PlatformFunctionDialog';

const WINDOWS = [1, 7, 30, 90];
const PRESET_HELP: Record<string, string> = {
  relaxed: 'Slowest 1% × 1.5. Rarely cuts a run off.',
  balanced: 'Slowest 5% × 1.5. The default.',
  strict: 'Slowest 5% × 1.2. Gives up sooner and moves to a backup model faster.',
};
const MODE_LABELS: Record<string, string> = {
  off: 'No time limit set',
  fixed: 'Fixed limit',
  auto: 'Learned from history',
};

const toS = (ms: number | null) => (ms === null ? null : Math.round(ms / 100) / 10);

interface Props {
  functionId: string;
  customerId?: string | null;
}

export const FunctionPerformance: React.FC<Props> = ({ functionId, customerId }) => {
  const { getIdToken, permissions, isImpersonating } = useAuth();
  const api = useMemo(() => performanceApi(getIdToken), [getIdToken]);
  const [days, setDays] = usePersistentState('functionPerformance.days', 30, WINDOWS);
  const [data, setData] = useState<Perf | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reliabilityOpen, setReliabilityOpen] = useState(false);
  const [platformOpen, setPlatformOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .function(functionId, days, customerId)
      .then(d => !cancelled && setData(d))
      .catch(e => !cancelled && setError(e instanceof Error ? e.message : 'Failed to load performance'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [api, functionId, days, customerId, reloadKey]);

  const unit = data?.window.unit ?? 'day';

  const rows = useMemo(
    () =>
      (data?.series ?? []).map(b => {
        const failed = b.runs - b.ok;
        return {
          label: fmtBucket(b.bucket, unit),
          bucket: b.bucket,
          p50: toS(b.p50_ms),
          p95: toS(b.p95_ms),
          p99: toS(b.p99_ms),
          limit: b.learned_limit_s,
          timedOut: b.timed_out || null,
          requested: Math.max(0, b.ok - b.fallback - b.zdr_swap),
          backup: b.fallback,
          privacy: b.zdr_swap,
          timeout: b.timed_out,
          failed: Math.max(0, failed - b.timed_out),
          perRun: b.runs ? Math.round((b.input_tokens + b.output_tokens) / b.runs) : null,
          per1k: b.credits_per_1k_runs,
        };
      }),
    [data, unit]
  );

  const cycleMarkers = useMemo(() => {
    if (!data?.learning?.cycles?.length || !rows.length) return [];
    const starts = rows.map(r => new Date(r.bucket).getTime());
    return data.learning.cycles
      .map(c => {
        const t = new Date(c.at).getTime();
        let idx = -1;
        starts.forEach((s, i) => {
          if (s <= t) idx = i;
        });
        return idx >= 0 ? { label: rows[idx].label, cycle: c } : null;
      })
      .filter((x): x is NonNullable<typeof x> => x !== null);
  }, [data, rows]);

  if (loading && !data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return null;

  const s = data.summary;
  const tl = data.time_limit;
  const fn = data.function;
  const headroom = fn.platform_timeout_s && tl.current_limit_s ? fn.platform_timeout_s - tl.current_limit_s : null;
  // The dialog edits the signed-in account's own settings.
  const canEditReliability = !customerId && !fn.is_public;
  const canMarkPlatform = isAdminUser(permissions?.userType) && !isImpersonating && fn.platform_builtin !== undefined && !fn.is_public;

  return (
    <Box sx={{ opacity: loading ? 0.6 : 1, transition: 'opacity 120ms' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="body2" color="text.secondary">
          All versions of this function, {unit === 'hour' ? 'per hour' : 'per day'}. Times are for successful runs.
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          {canMarkPlatform &&
            (fn.platform_builtin ? (
              <Tooltip title="Defined in code (PLATFORM_ROUTES), so its platform settings can't be changed here.">
                <Chip size="small" variant="outlined" icon={<HubIcon />} label="Built-in platform function" />
              </Tooltip>
            ) : (
              <Button size="small" variant="outlined" startIcon={<HubIcon />} onClick={() => setPlatformOpen(true)}>
                {fn.platform_mark ? 'Platform function settings' : 'Mark as platform function'}
              </Button>
            ))}
          <ToggleButtonGroup size="small" exclusive value={days} onChange={(_, v) => v && setDays(v)}>
            {WINDOWS.map(d => (
              <ToggleButton key={d} value={d}>
                {d === 1 ? '24h' : `${d}d`}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>
      </Box>

      {fn.platform_tool && (
        <Alert severity={headroom !== null && headroom < 5 ? 'warning' : 'info'} sx={{ mb: 2 }}>
          Platform route <b>{fn.platform_tool}</b>
          {fn.platform_timeout_s
            ? `. The Passio platform waits up to ${fn.platform_timeout_s}s for an answer from EasyAI. ${
                headroom === null
                  ? "This function has no time limit, so a slow model could run past the platform's wait and the platform would give up first."
                  : headroom > 0
                    ? `This function's time limit (${fmtS(tl.current_limit_s)}, all attempts and backups included) ends ${fmtS(headroom)} before that, so the platform always gets an answer or an error from us first.`
                    : `This function's time limit (${fmtS(tl.current_limit_s)}) is longer than that, so the platform can give up before we answer. Keep it under ${fn.platform_timeout_s}s.`
              }`
            : fn.platform_kind === 'regional_search'
              ? ' · regional semantic search'
              : ". No platform wait is set, so there's no headroom check."}
        </Alert>
      )}

      {!s || s.runs === 0 ? (
        <Alert severity="info">No runs in this window.</Alert>
      ) : (
        <>
          <Grid container spacing={1.5} sx={{ mb: 3 }}>
            <Grid item xs={6} md={2}>
              <StatCard label="Runs" value={fmtNum(s.runs)} caption={`${fmtPct(s.success_rate)} succeeded`} />
            </Grid>
            <Grid item xs={6} md={2}>
              <StatCard
                label="Typical / slow / slowest"
                value={fmtMs(s.p50_ms)}
                caption={`p95 ${fmtMs(s.p95_ms)} · p99 ${fmtMs(s.p99_ms)}`}
                tooltip="p50 is the typical run. p95 and p99: only 5% and 1% of successful runs took longer."
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <StatCard
                label="Answered by backup"
                value={fmtPct(s.backup_share)}
                caption={`${fmtNum(s.fallback)} runs`}
                tooltip="The first model failed or was too slow, and a backup model answered instead."
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <StatCard
                label="Timed out"
                value={fmtPct(s.timeout_share)}
                caption={`${fmtNum(s.timed_out)} runs`}
                tooltip="Runs that hit a time limit and returned an error."
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <StatCard
                label="Invalid output"
                value={fmtPct(s.invalid_output_share)}
                caption={`${fmtNum(s.bad_output)} runs`}
                tooltip="A model returned output that didn't match the response format (often fixed by a retry)."
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <StatCard
                label="Credits per 1,000 runs"
                value={fmtCredits(s.credits_per_1k_runs)}
                caption={`${fmtCredits(s.credits)} total · ${fmtNum(Math.round((s.input_tokens + s.output_tokens) / s.runs))} tokens/run`}
              />
            </Grid>
          </Grid>

          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <SectionTitle
              title="Latency and time limit"
              help="Successful run times, the automatic time limit learned from them, and runs that timed out. Purple lines mark self-improvement cycles."
            />
            <Box sx={{ height: 300 }}>
              <ResponsiveContainer>
                <ComposedChart data={rows} margin={{ top: 10, right: 8, left: -8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} minTickGap={16} />
                  <YAxis yAxisId="s" tick={{ fontSize: 11 }} unit="s" />
                  <YAxis yAxisId="n" orientation="right" tick={{ fontSize: 11 }} allowDecimals={false} />
                  <ChartTooltip />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar yAxisId="n" dataKey="timedOut" name="Timed out (runs)" fill="#e57373" barSize={8} />
                  <Line yAxisId="s" dataKey="p50" name="p50" stroke="#90caf9" dot={false} connectNulls />
                  <Line yAxisId="s" dataKey="p95" name="p95" stroke="#1976d2" dot={false} connectNulls />
                  <Line yAxisId="s" dataKey="p99" name="p99" stroke="#0d47a1" dot={false} strokeDasharray="2 2" connectNulls />
                  <Line yAxisId="s" type="stepAfter" dataKey="limit" name="Learned time limit" stroke="#f57c00" strokeWidth={2} dot={false} strokeDasharray="6 3" connectNulls />
                  {fn.platform_timeout_s && (
                    <ReferenceLine yAxisId="s" y={fn.platform_timeout_s} stroke="#d32f2f" strokeDasharray="4 4" label={{ value: 'Platform timeout', fontSize: 11, fill: '#d32f2f', position: 'insideTopRight' }} />
                  )}
                  {cycleMarkers.map((m, i) => (
                    <ReferenceLine key={i} yAxisId="s" x={m.label} stroke="#7b1fa2" strokeDasharray="3 3" />
                  ))}
                </ComposedChart>
              </ResponsiveContainer>
            </Box>
          </Paper>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={7}>
              <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                <SectionTitle title="Outcomes" help="How each run ended." />
                <Box sx={{ height: 240 }}>
                  <ResponsiveContainer>
                    <ComposedChart data={rows} margin={{ top: 10, right: 8, left: -12, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} minTickGap={16} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <ChartTooltip />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar dataKey="requested" stackId="o" name="Requested model" fill="#66bb6a" />
                      <Bar dataKey="backup" stackId="o" name="Backup model" fill="#ffb74d" />
                      <Bar dataKey="privacy" stackId="o" name="Privacy substitute" fill="#90a4ae" />
                      <Bar dataKey="timeout" stackId="o" name="Timed out" fill="#e57373" />
                      <Bar dataKey="failed" stackId="o" name="Failed" fill="#b71c1c" />
                    </ComposedChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} md={5}>
              <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                <SectionTitle title="Cost and tokens" help="Credits per 1,000 runs and tokens per run." />
                <Box sx={{ height: 240 }}>
                  <ResponsiveContainer>
                    <ComposedChart data={rows} margin={{ top: 10, right: 0, left: -12, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} minTickGap={16} />
                      <YAxis yAxisId="c" tick={{ fontSize: 11 }} />
                      <YAxis yAxisId="t" orientation="right" tick={{ fontSize: 11 }} />
                      <ChartTooltip />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Line yAxisId="c" dataKey="per1k" name="Credits / 1k runs" stroke="#2e7d32" dot={false} connectNulls />
                      <Line yAxisId="t" dataKey="perRun" name="Tokens / run" stroke="#6d4c41" dot={false} connectNulls />
                    </ComposedChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
              <SectionTitle title="Time limit" help="Set in the function's Reliability settings." />
              {canEditReliability && (
                <Button size="small" onClick={() => setReliabilityOpen(true)}>
                  Reliability settings
                </Button>
              )}
            </Stack>
            <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', rowGap: 1 }}>
              <Chip size="small" label={MODE_LABELS[tl.mode] || tl.mode} />
              {tl.mode === 'auto' && <Chip size="small" label={`Preset: ${tl.preset}`} />}
              <Chip size="small" color="primary" label={`Now: ${fmtS(tl.current_limit_s)}`} />
              {tl.mode !== 'auto' && <Chip size="small" variant="outlined" label={`Automatic would be ${fmtS(tl.auto_limit_s)}`} />}
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {tl.current_note}
            </Typography>
            <Typography variant="subtitle2" gutterBottom>
              What each preset would do
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Preset</TableCell>
                  <TableCell align="right">Limit</TableCell>
                  <TableCell align="right">Recent runs cut off</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.what_if.map(w => (
                  <TableRow key={w.preset} selected={tl.mode === 'auto' && tl.preset === w.preset}>
                    <TableCell>
                      <Tooltip title={PRESET_HELP[w.preset] || ''}>
                        <span style={{ cursor: 'help' }}>{w.preset}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">{fmtS(w.limit_s)}</TableCell>
                    <TableCell align="right">
                      {w.would_cut_off === null ? '–' : `${fmtNum(w.would_cut_off)} of ${fmtNum(w.runs_checked)} (${fmtPct(w.cut_off_share)})`}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Typography variant="caption" color="text.secondary">
              Checked against up to 1,000 recent successful runs. A cut-off run would move to a backup model or fail, depending on the
              failure setting.
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
            <SectionTitle title="Requested vs answered model" help="Which model was asked for, picked, and actually answered." />
            {data.routes.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No runs.
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Route</TableCell>
                    <TableCell align="right">Runs</TableCell>
                    <TableCell align="right">Succeeded</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.routes.slice(0, 10).map((r, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                          {r.requested && r.requested !== r.selected && (
                            <>
                              <Chip size="small" variant="outlined" label={r.requested} />
                              <ArrowIcon sx={{ fontSize: 14 }} />
                            </>
                          )}
                          <Chip size="small" variant="outlined" label={r.selected || 'default'} />
                          {r.answered !== r.selected &&
                            (r.upgraded ? (
                              <Tooltip title="Replacement for a deprecated model, not a backup">
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                  <ArrowIcon sx={{ fontSize: 14 }} />
                                  <Chip size="small" color="info" variant="outlined" label={`${r.answered} (upgrade)`} />
                                </Stack>
                              </Tooltip>
                            ) : (
                              <>
                                <ArrowIcon sx={{ fontSize: 14, color: 'warning.main' }} />
                                <Chip size="small" color="warning" variant="outlined" label={r.answered || '–'} />
                              </>
                            ))}
                        </Stack>
                      </TableCell>
                      <TableCell align="right">{fmtNum(r.runs)}</TableCell>
                      <TableCell align="right">{fmtPct(r.runs ? r.ok / r.runs : null)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
            <SectionTitle title="By model" />
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Model</TableCell>
                  <TableCell align="right">Runs</TableCell>
                  <TableCell align="right">Success</TableCell>
                  <TableCell align="right">p50</TableCell>
                  <TableCell align="right">p95</TableCell>
                  <TableCell align="right">Credits / 1k</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.models.map(m => (
                  <TableRow key={m.model_used || 'none'}>
                    <TableCell>{m.model_used || '–'}</TableCell>
                    <TableCell align="right">{fmtNum(m.runs)}</TableCell>
                    <TableCell align="right">{fmtPct(m.success_rate)}</TableCell>
                    <TableCell align="right">{fmtMs(m.p50_ms)}</TableCell>
                    <TableCell align="right">{fmtMs(m.p95_ms)}</TableCell>
                    <TableCell align="right">{fmtCredits(m.credits_per_1k_runs)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Grid>
        <Grid item xs={12} md={5}>
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <SectionTitle title="Errors" />
            {data.errors.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No failed runs.
              </Typography>
            ) : (
              data.errors.map(e => (
                <Box key={e.label} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.25 }}>
                  <Typography variant="body2">{e.label}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {fmtNum(e.runs)}
                  </Typography>
                </Box>
              ))
            )}
          </Paper>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <SectionTitle title="Self-improvement" />
            {!data.learning ? (
              <Typography variant="body2" color="text.secondary">
                Not set up for this function.
              </Typography>
            ) : (
              <>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {data.learning.enabled ? 'On' : 'Off'}
                  {data.learning.last_cycle_at && ` · last cycle ${new Date(data.learning.last_cycle_at).toLocaleString()} (${data.learning.last_cycle_status})`}
                </Typography>
                {(data.learning.cycles ?? []).slice(-5).reverse().map((c, i) => (
                  <Typography key={i} variant="caption" color="text.secondary" component="div">
                    {new Date(c.at).toLocaleDateString()} · {c.status} · {c.trigger}
                    {Object.keys(c.summary).length > 0 && ` · ${Object.entries(c.summary).map(([k, v]) => `${k.replace(/_/g, ' ')} ${v}`).join(', ')}`}
                  </Typography>
                ))}
              </>
            )}
          </Paper>
        </Grid>
      </Grid>
      {reliabilityOpen && (
        <ReliabilityDialog
          open
          onClose={() => setReliabilityOpen(false)}
          targetType="function"
          targetId={fn.id}
          targetName={fn.name}
          onSaved={() => setReloadKey(k => k + 1)}
        />
      )}
      {platformOpen && (
        <PlatformFunctionDialog
          open
          onClose={() => setPlatformOpen(false)}
          functionId={fn.id}
          functionName={fn.name}
          mark={fn.platform_mark ?? null}
          onSaved={() => setReloadKey(k => k + 1)}
        />
      )}
    </Box>
  );
};
